import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
    UserPlus, RefreshCw, Ban, CheckCircle2, FileText, ShieldCheck, Building2, UserX,
    ExternalLink, Pencil, Trash2, ShieldAlert, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const REGIME_LABELS = {
    fisica: 'Persona Física (RFC)',
    moral: 'Persona Moral / Empresa',
    sin_rfc: 'Sin RFC',
};

const REGIME_ICONS = {
    fisica: ShieldCheck,
    moral: Building2,
    sin_rfc: UserX,
};

const STATUS_BADGE = {
    active:    { label: 'Activo',     cls: 'bg-emerald-100 text-emerald-800' },
    suspended: { label: 'Suspendido', cls: 'bg-amber-100 text-amber-800' },
    banned:    { label: 'Baneado',    cls: 'bg-red-100 text-red-800' },
    deleted:   { label: 'Eliminado',  cls: 'bg-gray-100 text-gray-500' },
};

const KYC_BADGE = {
    approved: { label: 'KYC ✓',     cls: 'bg-emerald-100 text-emerald-800' },
    pending:  { label: 'KYC pendiente', cls: 'bg-amber-100 text-amber-800' },
    rejected: { label: 'KYC rechazado', cls: 'bg-red-100 text-red-800' },
};

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState('');

    // Create
    const [createOpen, setCreateOpen] = useState(false);
    const [newU, setNewU] = useState({ nickname: '', email: '', full_name: '', phone: '', location: '' });
    const [createdInfo, setCreatedInfo] = useState(null);

    // Edit
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editBusy, setEditBusy] = useState(false);

    // Delete
    const [deleteUser, setDeleteUser] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    // Fiscal profile
    const [fiscalDialogUser, setFiscalDialogUser] = useState(null);
    const [fiscalBusy, setFiscalBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get('/admin/users', { params: q ? { q } : {} });
        setUsers(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

    // ── Create ──────────────────────────────────────────────
    const create = async () => {
        try {
            const { data } = await api.post('/admin/users', newU);
            setCreatedInfo({ nickname: data.nickname, initial_pin: data.initial_pin });
            setNewU({ nickname: '', email: '', full_name: '', phone: '', location: '' });
            setCreateOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    // ── Edit ────────────────────────────────────────────────
    const openEdit = (u) => {
        setEditUser(u);
        setEditForm({
            full_name: u.full_name || '',
            email: u.email || '',
            phone: u.phone || '',
            location: u.location || '',
            status: u.status || 'active',
            verified: !!u.verified,
        });
    };

    const saveEdit = async () => {
        setEditBusy(true);
        try {
            await api.put(`/admin/users/${editUser.id}`, {
                full_name: editForm.full_name || null,
                email: editForm.email || null,
                phone: editForm.phone || null,
                location: editForm.location || null,
                status: editForm.status,
                verified: editForm.verified,
            });
            toast.success('Usuario actualizado');
            setEditUser(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally { setEditBusy(false); }
    };

    // ── Status shortcuts ─────────────────────────────────────
    const suspend = async (u) => {
        const status = u.status === 'active' ? 'suspended' : 'active';
        await api.put(`/admin/users/${u.id}`, { status });
        toast.success(status === 'suspended' ? 'Cuenta suspendida' : 'Cuenta reactivada');
        load();
    };

    const ban = async (u) => {
        if (!window.confirm(`¿Banear permanentemente a @${u.nickname}? Esta acción es más severa que suspender.`)) return;
        await api.put(`/admin/users/${u.id}`, { status: 'banned' });
        toast.success(`@${u.nickname} baneado`);
        load();
    };

    const resetPin = async (u) => {
        if (!window.confirm(`¿Resetear PIN de @${u.nickname} a 9999?`)) return;
        await api.post(`/admin/users/${u.id}/reset-pin`);
        toast.success('PIN reseteado a 9999');
    };

    // ── Delete ───────────────────────────────────────────────
    const confirmDelete = async () => {
        setDeleteBusy(true);
        try {
            await api.delete(`/admin/users/${deleteUser.id}`);
            toast.success(`Cuenta de @${deleteUser.nickname} eliminada`);
            setDeleteUser(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al eliminar');
        } finally { setDeleteBusy(false); }
    };

    // ── Fiscal toggle ────────────────────────────────────────
    const toggleFiscal = async () => {
        if (!fiscalDialogUser) return;
        const newActive = !fiscalDialogUser.fiscal_profile_active;
        setFiscalBusy(true);
        try {
            await api.put(`/admin/users/${fiscalDialogUser.id}/fiscal-profile`, null, { params: { active: newActive } });
            toast.success(newActive ? 'Perfil fiscal activado' : 'Perfil fiscal desactivado');
            setFiscalDialogUser(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cambiar perfil fiscal');
        } finally { setFiscalBusy(false); }
    };

    // ── Helpers ──────────────────────────────────────────────
    const hasFiscalDocs = (u) => !!(u.fiscal_csf_url || u.fiscal_bank_statement_url);
    const hasKycDocs = (u) => !!(u.kyc_status || u.document_front_url);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Usuarios</h1>
                    <p className="text-sm text-muted-foreground">Registro y gestión de usuarios.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Input placeholder="Buscar por nickname/email…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="users-search" />
                    <Button onClick={() => setCreateOpen(true)} data-testid="users-create-btn"><UserPlus className="h-4 w-4 mr-1" />Crear usuario</Button>
                </div>
            </div>

            {/* Table */}
            <div className="rounded-xl border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground text-left border-b">
                        <tr>
                            <th className="p-3">Nickname</th>
                            <th className="p-3">Nombre</th>
                            <th className="p-3">Email</th>
                            <th className="p-3">Estado</th>
                            <th className="p-3">Verificación</th>
                            <th className="p-3">Perfil Fiscal</th>
                            <th className="p-3">Rating</th>
                            <th className="p-3">Ventas</th>
                            <th className="p-3 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody data-testid="users-table-body">
                        {users.map(u => {
                            const sb = STATUS_BADGE[u.status] || STATUS_BADGE.active;
                            const kycBadge = KYC_BADGE[u.kyc_status];
                            const fiscalPending = hasFiscalDocs(u) && !u.fiscal_profile_active;
                            const kycPending = hasKycDocs(u) && u.kyc_status === 'pending';
                            return (
                                <tr key={u.id} className={`border-b ${u.status === 'deleted' ? 'opacity-50' : ''}`}>
                                    {/* Nickname */}
                                    <td className="p-3 font-medium">
                                        <div className="flex items-center gap-1.5">
                                            <Link to={`/u/${u.nickname}`} className="text-red-600 hover:underline" target="_blank" rel="noreferrer">@{u.nickname}</Link>
                                            {u.verified && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" title="Verificado" />}
                                        </div>
                                    </td>
                                    <td className="p-3">{u.full_name || '-'}</td>
                                    <td className="p-3 text-xs">{u.email || '-'}</td>

                                    {/* Status */}
                                    <td className="p-3">
                                        <Badge className={sb.cls}>{sb.label}</Badge>
                                    </td>

                                    {/* Verification indicators */}
                                    <td className="p-3">
                                        <div className="flex flex-col gap-1">
                                            {/* KYC identity */}
                                            {kycBadge ? (
                                                <Badge className={`text-[10px] ${kycBadge.cls}`}>{kycBadge.label}</Badge>
                                            ) : kycPending ? (
                                                <Badge className="text-[10px] bg-amber-100 text-amber-800">
                                                    <Clock className="h-2.5 w-2.5 mr-0.5" />KYC pendiente
                                                </Badge>
                                            ) : null}
                                            {/* Fiscal docs submitted */}
                                            {fiscalPending && (
                                                <Badge className="text-[10px] bg-blue-100 text-blue-800">
                                                    <FileText className="h-2.5 w-2.5 mr-0.5" />Fiscal enviado
                                                </Badge>
                                            )}
                                        </div>
                                    </td>

                                    {/* Fiscal profile */}
                                    <td className="p-3">
                                        {u.fiscal_regime ? (
                                            <button
                                                onClick={() => setFiscalDialogUser(u)}
                                                data-testid={`fiscal-toggle-${u.nickname}`}
                                                className="flex items-center gap-1.5"
                                            >
                                                <Badge className={u.fiscal_profile_active
                                                    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 cursor-pointer'
                                                    : 'bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer'}>
                                                    {u.fiscal_profile_active
                                                        ? <><CheckCircle2 className="h-3 w-3 mr-1" />Activo</>
                                                        : <><FileText className="h-3 w-3 mr-1" />Pendiente</>}
                                                </Badge>
                                            </button>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Sin datos</span>
                                        )}
                                    </td>

                                    <td className="p-3 tabular-nums">{u.rating_avg?.toFixed(1) || '0.0'} ({u.rating_count || 0})</td>
                                    <td className="p-3 tabular-nums">{u.total_sales || 0}</td>

                                    {/* Actions */}
                                    <td className="p-3">
                                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                            {/* Edit */}
                                            <Button size="sm" variant="outline" onClick={() => openEdit(u)} title="Editar" data-testid={`edit-${u.nickname}`}>
                                                <Pencil className="h-3 w-3" />
                                            </Button>
                                            {/* Reset PIN */}
                                            <Button size="sm" variant="outline" onClick={() => resetPin(u)} title="Resetear PIN" data-testid={`reset-pin-${u.nickname}`}>
                                                <RefreshCw className="h-3 w-3" />
                                            </Button>
                                            {/* Suspend / Reactivate */}
                                            {u.status !== 'deleted' && (
                                                <Button size="sm" variant="outline" onClick={() => suspend(u)} title={u.status === 'active' ? 'Suspender' : 'Reactivar'} data-testid={`toggle-status-${u.nickname}`}>
                                                    {u.status === 'active' ? <Ban className="h-3 w-3 text-amber-600" /> : <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                                                </Button>
                                            )}
                                            {/* Ban */}
                                            {u.status !== 'banned' && u.status !== 'deleted' && (
                                                <Button size="sm" variant="outline" onClick={() => ban(u)} title="Banear cuenta" data-testid={`ban-${u.nickname}`}>
                                                    <ShieldAlert className="h-3 w-3 text-red-600" />
                                                </Button>
                                            )}
                                            {/* Delete */}
                                            {u.status !== 'deleted' && (
                                                <Button size="sm" variant="outline" onClick={() => setDeleteUser(u)} title="Eliminar cuenta" data-testid={`delete-${u.nickname}`}>
                                                    <Trash2 className="h-3 w-3 text-red-600" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── EDIT DIALOG ─────────────────────────────────────── */}
            <Dialog open={!!editUser} onOpenChange={(v) => { if (!v) setEditUser(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil className="h-4 w-4 text-red-600" />
                            Editar usuario — @{editUser?.nickname}
                        </DialogTitle>
                        <DialogDescription>Modifica los datos del usuario. El nickname no se puede cambiar.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Label>Nombre completo</Label>
                            <Input value={editForm.full_name || ''} onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))} data-testid="edit-full-name" />
                        </div>
                        <div className="col-span-2">
                            <Label>Email</Label>
                            <Input type="email" value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} data-testid="edit-email" />
                        </div>
                        <div>
                            <Label>Teléfono</Label>
                            <Input value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} data-testid="edit-phone" />
                        </div>
                        <div>
                            <Label>Ubicación</Label>
                            <Input value={editForm.location || ''} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} data-testid="edit-location" />
                        </div>
                        <div>
                            <Label>Estado de cuenta</Label>
                            <Select value={editForm.status || 'active'} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                                <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">Activo</SelectItem>
                                    <SelectItem value="suspended">Suspendido</SelectItem>
                                    <SelectItem value="banned">Baneado</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2 pt-5">
                            <Switch checked={!!editForm.verified} onCheckedChange={v => setEditForm(f => ({ ...f, verified: v }))} id="edit-verified" data-testid="edit-verified" />
                            <Label htmlFor="edit-verified">Cuenta verificada</Label>
                        </div>
                    </div>
                    <DialogFooter className="mt-2">
                        <Button variant="outline" onClick={() => setEditUser(null)} disabled={editBusy}>Cancelar</Button>
                        <Button onClick={saveEdit} disabled={editBusy} className="bg-red-600 hover:bg-red-700" data-testid="edit-save">
                            {editBusy ? 'Guardando…' : 'Guardar cambios'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── DELETE CONFIRM DIALOG ────────────────────────────── */}
            <Dialog open={!!deleteUser} onOpenChange={(v) => { if (!v) setDeleteUser(null); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 className="h-5 w-5" />Eliminar cuenta
                        </DialogTitle>
                        <DialogDescription>
                            Esta acción es permanente. La cuenta de <strong>@{deleteUser?.nickname}</strong> será marcada como eliminada y todos sus anuncios serán pausados.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-800">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        Esta operación no puede deshacerse desde esta interfaz.
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteUser(null)} disabled={deleteBusy}>Cancelar</Button>
                        <Button variant="destructive" onClick={confirmDelete} disabled={deleteBusy} data-testid="delete-confirm">
                            {deleteBusy ? 'Eliminando…' : 'Sí, eliminar cuenta'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── FISCAL TOGGLE DIALOG ─────────────────────────────── */}
            <Dialog open={!!fiscalDialogUser} onOpenChange={(open) => { if (!open) setFiscalDialogUser(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-red-600" />
                            Perfil Fiscal — @{fiscalDialogUser?.nickname}
                        </DialogTitle>
                        <DialogDescription>
                            Revisa los datos fiscales del vendedor antes de{' '}
                            {fiscalDialogUser?.fiscal_profile_active ? 'desactivar' : 'activar'} su perfil.
                        </DialogDescription>
                    </DialogHeader>

                    {fiscalDialogUser && (
                        <div className="space-y-3 text-sm">
                            <div className="rounded-lg border p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const Icon = REGIME_ICONS[fiscalDialogUser.fiscal_regime] || UserX;
                                        return <Icon className="h-4 w-4 text-muted-foreground" />;
                                    })()}
                                    <span className="font-medium">
                                        {REGIME_LABELS[fiscalDialogUser.fiscal_regime] || fiscalDialogUser.fiscal_regime || 'Sin régimen'}
                                    </span>
                                </div>
                                {fiscalDialogUser.tax_id && (
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                        <span className="text-muted-foreground">RFC</span>
                                        <span className="font-mono font-medium">{fiscalDialogUser.tax_id}</span>
                                        {fiscalDialogUser.fiscal_curp && (
                                            <>
                                                <span className="text-muted-foreground">CURP</span>
                                                <span className="font-mono font-medium">{fiscalDialogUser.fiscal_curp}</span>
                                            </>
                                        )}
                                    </div>
                                )}
                                {(fiscalDialogUser.fiscal_csf_url || fiscalDialogUser.fiscal_bank_statement_url) && (
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {fiscalDialogUser.fiscal_csf_url && (
                                            <a href={fiscalDialogUser.fiscal_csf_url} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-red-600 underline">
                                                <ExternalLink className="h-3 w-3" />CSF (Constancia SAT)
                                            </a>
                                        )}
                                        {fiscalDialogUser.fiscal_bank_statement_url && (
                                            <a href={fiscalDialogUser.fiscal_bank_statement_url} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-red-600 underline">
                                                <ExternalLink className="h-3 w-3" />Estado de cuenta
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between rounded-lg border p-3">
                                <span className="text-muted-foreground">Estado actual</span>
                                <Badge className={fiscalDialogUser.fiscal_profile_active
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-amber-100 text-amber-800'}>
                                    {fiscalDialogUser.fiscal_profile_active ? 'Activo' : 'Pendiente'}
                                </Badge>
                            </div>

                            {fiscalDialogUser.fiscal_profile_active && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                    Al desactivar, el vendedor pasará a tasas máximas (ISR 20% + IVA 16%) en sus próximas ventas.
                                </p>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFiscalDialogUser(null)} disabled={fiscalBusy}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={toggleFiscal}
                            disabled={fiscalBusy}
                            className={fiscalDialogUser?.fiscal_profile_active
                                ? 'bg-amber-600 hover:bg-amber-700 text-white'
                                : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                            data-testid="fiscal-toggle-confirm"
                        >
                            {fiscalBusy ? 'Guardando…' : fiscalDialogUser?.fiscal_profile_active ? 'Desactivar perfil' : 'Activar perfil'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── CREATE USER DIALOG ───────────────────────────────── */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Crear usuario</DialogTitle>
                        <DialogDescription>Se asignará el PIN inicial <span className="font-mono font-semibold">9999</span>. El usuario deberá cambiarlo al primer acceso.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label>Nickname *</Label><Input value={newU.nickname} onChange={(e) => setNewU({ ...newU, nickname: e.target.value })} data-testid="new-user-nickname" /></div>
                        <div className="col-span-2"><Label>Nombre completo</Label><Input value={newU.full_name} onChange={(e) => setNewU({ ...newU, full_name: e.target.value })} /></div>
                        <div className="col-span-2"><Label>Email</Label><Input value={newU.email} onChange={(e) => setNewU({ ...newU, email: e.target.value })} /></div>
                        <div><Label>Teléfono</Label><Input value={newU.phone} onChange={(e) => setNewU({ ...newU, phone: e.target.value })} /></div>
                        <div><Label>Ubicación</Label><Input value={newU.location} onChange={(e) => setNewU({ ...newU, location: e.target.value })} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
                        <Button onClick={create} data-testid="new-user-submit">Crear</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── CREATED INFO DIALOG ─────────────────────────────── */}
            <Dialog open={!!createdInfo} onOpenChange={() => setCreatedInfo(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Usuario creado</DialogTitle></DialogHeader>
                    <div className="space-y-2 text-sm">
                        <div>Nickname: <span className="font-mono font-semibold">@{createdInfo?.nickname}</span></div>
                        <div>PIN inicial: <span className="font-mono font-semibold text-lg">{createdInfo?.initial_pin}</span></div>
                        <p className="text-muted-foreground">Comparte estas credenciales con el usuario. Debe cambiar el PIN al primer inicio de sesión.</p>
                    </div>
                    <DialogFooter><Button onClick={() => setCreatedInfo(null)}>Entendido</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
